import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Sale, Payment, Customer } from '../types';
import { toDate } from './helpers';
import { format } from 'date-fns';

export interface ReportPDFConfig {
  startDate: Date;
  endDate: Date;
  sections: {
    keyMetrics: boolean;
    ordersTable: boolean;
    paymentSummary: boolean;
    topCustomers: boolean;
    statusDistribution: boolean;
  };
}

export interface ReportPDFData {
  orders: Sale[];
  payments: Payment[];
  customers: Customer[];
  contactInfo: {
    primaryPhone: string;
    secondaryPhone: string;
    email: string;
    address: string;
  };
  logoBase64: string;
}

interface ReportStats {
  totalRevenue: number;
  totalOrders: number;
  totalCustomers: number;
  totalPaid: number;
  outstandingAmount: number;
  collectionRate: number;
}

// Brand colors
const GREEN = [22, 163, 74] as const;
const DARK = [31, 41, 55] as const;
const GRAY = [107, 114, 128] as const;
const LIGHT_BG = [249, 250, 251] as const;
const TABLE_HEADER_BG = [22, 163, 74] as const;
const FOOTER_BG = [243, 244, 246] as const;

const PAGE_WIDTH = 210; // A4 mm
const MARGIN = 15;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

function calculateStats(data: ReportPDFData): ReportStats {
  const totalRevenue = data.orders.reduce((sum, o) => sum + o.totalAmount, 0);
  const totalPaid = data.payments.reduce((sum, p) => sum + p.amount, 0);
  const outstandingAmount = data.orders.reduce((sum, o) => sum + o.remainingAmount, 0);
  return {
    totalRevenue,
    totalOrders: data.orders.length,
    totalCustomers: data.customers.length,
    totalPaid,
    outstandingAmount,
    collectionRate: totalRevenue > 0 ? Math.round((totalPaid / totalRevenue) * 100) : 0,
  };
}

function buildCustomerMap(customers: Customer[]): Map<string, string> {
  const map = new Map<string, string>();
  customers.forEach(c => map.set(c.id, c.name));
  return map;
}

function formatPdfCurrency(amount: number): string {
  // Use Rs. prefix for PDF since jsPDF default fonts may not render ₹
  return `Rs. ${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatPdfDate(dateValue: any): string {
  if (!dateValue) return '-';
  const date = toDate(dateValue);
  return format(date, 'MMM dd, yyyy');
}

function checkAddPage(doc: jsPDF, currentY: number, requiredSpace: number): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + requiredSpace > pageHeight - 20) {
    doc.addPage();
    return 15;
  }
  return currentY;
}

function renderSectionTitle(doc: jsPDF, title: string, y: number): number {
  y = checkAddPage(doc, y, 20);
  // Green accent bar
  doc.setFillColor(...GREEN);
  doc.rect(MARGIN, y, 3, 7, 'F');
  doc.setFontSize(12);
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.text(title, MARGIN + 6, y + 5.5);
  return y + 12;
}

function renderHeader(doc: jsPDF, data: ReportPDFData, config: ReportPDFConfig, y: number): number {
  // Logo
  if (data.logoBase64) {
    try {
      doc.addImage(data.logoBase64, 'PNG', MARGIN, y, 14, 14);
    } catch {
      // Skip logo if it fails
    }
  }

  // Company name
  const textX = data.logoBase64 ? MARGIN + 17 : MARGIN;
  doc.setFontSize(18);
  doc.setTextColor(...GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text('Kariyas Nursery', textX, y + 7);

  // Tagline
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.text('Premium & High Quality Plants', textX, y + 12);

  // Contact info on the right
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  const rightX = PAGE_WIDTH - MARGIN;
  doc.text(data.contactInfo.address, rightX, y + 4, { align: 'right' });
  doc.text(`Phone: ${data.contactInfo.primaryPhone} | ${data.contactInfo.secondaryPhone}`, rightX, y + 8, { align: 'right' });
  doc.text(`Email: ${data.contactInfo.email}`, rightX, y + 12, { align: 'right' });

  y += 17;

  // Green separator line
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 5;

  // Report title
  doc.setFontSize(14);
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.text('BUSINESS REPORT', PAGE_WIDTH / 2, y + 2, { align: 'center' });
  y += 8;

  // Date range
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  const periodText = `Period: ${format(config.startDate, 'MMM dd, yyyy')} - ${format(config.endDate, 'MMM dd, yyyy')}`;
  doc.text(periodText, PAGE_WIDTH / 2, y, { align: 'center' });
  y += 5;

  const generatedText = `Generated: ${format(new Date(), 'MMM dd, yyyy')} at ${format(new Date(), 'hh:mm a')}`;
  doc.text(generatedText, PAGE_WIDTH / 2, y, { align: 'center' });
  y += 8;

  // Thin separator
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 6;

  return y;
}

function renderKeyMetrics(doc: jsPDF, stats: ReportStats, y: number): number {
  y = renderSectionTitle(doc, 'Key Metrics Summary', y);

  const metrics = [
    ['Total Revenue', formatPdfCurrency(stats.totalRevenue)],
    ['Total Orders', stats.totalOrders.toString()],
    ['Active Farmers', stats.totalCustomers.toString()],
    ['Outstanding Amount', formatPdfCurrency(stats.outstandingAmount)],
    ['Total Collected', formatPdfCurrency(stats.totalPaid)],
    ['Collection Rate', `${stats.collectionRate}%`],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Value']],
    body: metrics,
    theme: 'grid',
    headStyles: {
      fillColor: [...TABLE_HEADER_BG],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 9, textColor: [...DARK] },
    columnStyles: {
      0: { cellWidth: CONTENT_WIDTH * 0.55, fontStyle: 'bold' },
      1: { cellWidth: CONTENT_WIDTH * 0.45, halign: 'right' },
    },
    alternateRowStyles: { fillColor: [...LIGHT_BG] },
    margin: { left: MARGIN, right: MARGIN },
    styles: { cellPadding: 3 },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

function renderOrdersTable(doc: jsPDF, orders: Sale[], y: number): number {
  y = renderSectionTitle(doc, 'Orders Breakdown', y);

  if (orders.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('No orders found in this period.', MARGIN, y + 2);
    return y + 10;
  }

  // Totals
  const totalTrays = orders.reduce((s, o) => s + o.totalTrays, 0);
  const totalAmount = orders.reduce((s, o) => s + o.totalAmount, 0);
  const totalPaid = orders.reduce((s, o) => s + o.amountPaid, 0);
  const totalOutstanding = orders.reduce((s, o) => s + o.remainingAmount, 0);

  const body = orders.map((order, i) => [
    (i + 1).toString(),
    `#${order.orderId || (order.id ? order.id.slice(0, 8) : '-')}`,
    order.customerName || '-',
    formatPdfDate(order.orderDate),
    order.totalTrays.toString(),
    formatPdfCurrency(order.totalAmount),
    formatPdfCurrency(order.amountPaid),
    formatPdfCurrency(order.remainingAmount),
    order.status?.name || 'Pending',
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'Order ID', 'Customer', 'Date', 'Trays', 'Total', 'Paid', 'Outstanding', 'Status']],
    body,
    foot: [['', '', '', 'TOTALS', totalTrays.toString(), formatPdfCurrency(totalAmount), formatPdfCurrency(totalPaid), formatPdfCurrency(totalOutstanding), '']],
    headStyles: {
      fillColor: [...TABLE_HEADER_BG],
      textColor: 255,
      fontSize: 7.5,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 7, textColor: [...DARK] },
    footStyles: {
      fillColor: [...FOOTER_BG],
      textColor: [...DARK],
      fontStyle: 'bold',
      fontSize: 7.5,
    },
    alternateRowStyles: { fillColor: [...LIGHT_BG] },
    margin: { left: MARGIN, right: MARGIN },
    styles: { cellPadding: 2, overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 20 },
      2: { cellWidth: 28 },
      3: { cellWidth: 22 },
      4: { cellWidth: 12, halign: 'center' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
      7: { cellWidth: 24, halign: 'right' },
      8: { cellWidth: 22 },
    },
    didParseCell: function (data: any) {
      // Color-code the status column
      if (data.column.index === 8 && data.section === 'body') {
        const status = (data.cell.raw || '').toString().toLowerCase();
        if (status === 'delivered' || status === 'complete') {
          data.cell.styles.textColor = [22, 163, 74];
        } else if (status === 'in progress') {
          data.cell.styles.textColor = [37, 99, 235];
        } else if (status.includes('pending') || status === 'seedingpending') {
          data.cell.styles.textColor = [217, 119, 6];
        } else if (status === 'cancelled') {
          data.cell.styles.textColor = [220, 38, 38];
        } else if (status === 'ready') {
          data.cell.styles.textColor = [147, 51, 234];
        }
      }
    },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

function renderPaymentSummary(
  doc: jsPDF,
  payments: Payment[],
  stats: ReportStats,
  customerMap: Map<string, string>,
  y: number
): number {
  y = renderSectionTitle(doc, 'Payment Summary', y);

  // Summary metrics
  const summaryData = [
    ['Total Collected', formatPdfCurrency(stats.totalPaid)],
    ['Outstanding', formatPdfCurrency(stats.outstandingAmount)],
    ['Collection Rate', `${stats.collectionRate}%`],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Value']],
    body: summaryData,
    theme: 'grid',
    headStyles: {
      fillColor: [...TABLE_HEADER_BG],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 9, textColor: [...DARK] },
    columnStyles: {
      0: { cellWidth: CONTENT_WIDTH * 0.55, fontStyle: 'bold' },
      1: { cellWidth: CONTENT_WIDTH * 0.45, halign: 'right' },
    },
    alternateRowStyles: { fillColor: [...LIGHT_BG] },
    margin: { left: MARGIN, right: MARGIN },
    styles: { cellPadding: 3 },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // Payment details table
  if (payments.length > 0) {
    y = checkAddPage(doc, y, 25);
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Details', MARGIN + 6, y + 2);
    y += 6;

    const totalPayments = payments.reduce((s, p) => s + p.amount, 0);

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Customer', 'Amount', 'Method']],
      body: payments.map((p, i) => [
        (i + 1).toString(),
        formatPdfDate(p.paymentDate),
        customerMap.get(p.customerId) || '-',
        formatPdfCurrency(p.amount),
        p.paymentMethod || '-',
      ]),
      foot: [['', '', 'TOTAL', formatPdfCurrency(totalPayments), '']],
      headStyles: {
        fillColor: [...TABLE_HEADER_BG],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 7.5, textColor: [...DARK] },
      footStyles: {
        fillColor: [...FOOTER_BG],
        textColor: [...DARK],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [...LIGHT_BG] },
      margin: { left: MARGIN, right: MARGIN },
      styles: { cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 10 },
        3: { halign: 'right' },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('No payments recorded in this period.', MARGIN, y + 2);
    y += 10;
  }

  return y;
}

function renderTopCustomers(doc: jsPDF, data: ReportPDFData, y: number): number {
  y = renderSectionTitle(doc, 'Top Customers', y);

  // Calculate customer stats from orders in the date range
  const customerStatsMap = new Map<string, { name: string; phone: string; orders: number; spent: number }>();
  data.orders.forEach(order => {
    const cid = order.customerId;
    const existing = customerStatsMap.get(cid);
    if (existing) {
      existing.orders++;
      existing.spent += order.totalAmount;
    } else {
      const customer = data.customers.find(c => c.id === cid);
      customerStatsMap.set(cid, {
        name: order.customerName || customer?.name || '-',
        phone: customer?.phone || '-',
        orders: 1,
        spent: order.totalAmount,
      });
    }
  });

  const topCustomers = Array.from(customerStatsMap.values())
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 10);

  if (topCustomers.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('No customer data available for this period.', MARGIN, y + 2);
    return y + 10;
  }

  autoTable(doc, {
    startY: y,
    head: [['Rank', 'Customer', 'Phone', 'Orders', 'Total Spent']],
    body: topCustomers.map((c, i) => [
      (i + 1).toString(),
      c.name,
      c.phone,
      c.orders.toString(),
      formatPdfCurrency(c.spent),
    ]),
    headStyles: {
      fillColor: [...TABLE_HEADER_BG],
      textColor: 255,
      fontSize: 8,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 8, textColor: [...DARK] },
    alternateRowStyles: { fillColor: [...LIGHT_BG] },
    margin: { left: MARGIN, right: MARGIN },
    styles: { cellPadding: 2.5 },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      4: { halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

function renderStatusDistribution(doc: jsPDF, orders: Sale[], y: number): number {
  y = renderSectionTitle(doc, 'Order Status Distribution', y);

  if (orders.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text('No orders found in this period.', MARGIN, y + 2);
    return y + 10;
  }

  // Group by status
  const statusMap = new Map<string, { count: number; value: number }>();
  orders.forEach(order => {
    const statusName = order.status?.name || 'Pending';
    const existing = statusMap.get(statusName);
    if (existing) {
      existing.count++;
      existing.value += order.totalAmount;
    } else {
      statusMap.set(statusName, { count: 1, value: order.totalAmount });
    }
  });

  const total = orders.length;
  const totalValue = orders.reduce((s, o) => s + o.totalAmount, 0);

  const body = Array.from(statusMap.entries()).map(([status, data]) => [
    status,
    data.count.toString(),
    `${Math.round((data.count / total) * 100)}%`,
    formatPdfCurrency(data.value),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Status', 'Count', '% of Total', 'Total Value']],
    body,
    foot: [['Total', total.toString(), '100%', formatPdfCurrency(totalValue)]],
    headStyles: {
      fillColor: [...TABLE_HEADER_BG],
      textColor: 255,
      fontSize: 8.5,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 8.5, textColor: [...DARK] },
    footStyles: {
      fillColor: [...FOOTER_BG],
      textColor: [...DARK],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: [...LIGHT_BG] },
    margin: { left: MARGIN, right: MARGIN },
    styles: { cellPadding: 3 },
    columnStyles: {
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

function addPageNumbers(doc: jsPDF, config: ReportPDFConfig): void {
  const totalPages = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} of ${totalPages}  |  Kariyas Nursery - Business Report  |  Generated on ${format(new Date(), 'MMM dd, yyyy')}`,
      PAGE_WIDTH / 2,
      pageHeight - 8,
      { align: 'center' }
    );
  }
}

export async function generateReportPDF(
  data: ReportPDFData,
  config: ReportPDFConfig
): Promise<void> {
  const doc = new jsPDF('p', 'mm', 'a4');
  let currentY = 15;

  const stats = calculateStats(data);
  const customerMap = buildCustomerMap(data.customers);

  // 1. Header
  currentY = renderHeader(doc, data, config, currentY);

  // 2. Key Metrics
  if (config.sections.keyMetrics) {
    currentY = renderKeyMetrics(doc, stats, currentY);
  }

  // 3. Orders Breakdown
  if (config.sections.ordersTable) {
    currentY = renderOrdersTable(doc, data.orders, currentY);
  }

  // 4. Payment Summary
  if (config.sections.paymentSummary) {
    currentY = renderPaymentSummary(doc, data.payments, stats, customerMap, currentY);
  }

  // 5. Top Customers
  if (config.sections.topCustomers) {
    currentY = renderTopCustomers(doc, data, currentY);
  }

  // 6. Status Distribution
  if (config.sections.statusDistribution) {
    renderStatusDistribution(doc, data.orders, currentY);
  }

  // Page numbers
  addPageNumbers(doc, config);

  // Download
  const dateStr = format(config.startDate, 'yyyy-MM-dd');
  const endStr = format(config.endDate, 'yyyy-MM-dd');
  doc.save(`Kariyas_Nursery_Report_${dateStr}_to_${endStr}.pdf`);
}
